import { Maybe } from 'graphql/jsutils/Maybe';
import { TFunction } from 'i18next';

import { ActiveLeaders } from '@domain/clubs';

import { getOfficersPagePath, isClubRotaract } from '@use-cases/clubs';

import { isEditLevel } from '@utils/access-helpers';
import { isToday, printYear } from '@utils/datetime';

import {
  ActiveMembership,
  Club,
  IndividualOperationsAccessGql,
  MembershipType,
} from '@typings/graphql';

const NON_BREAKING_HYPHEN = '\u2011';

export const getActiveAssociatedClub = (
  associatedClubsInfo: Partial<Club>[],
  currentClubId: string,
  activeMembership?: Maybe<Omit<ActiveMembership, 'districtId'>>
) =>
  associatedClubsInfo.filter(
    ({ clubId }) =>
      clubId === activeMembership?.clubId &&
      currentClubId !== activeMembership?.clubId
  );

export const getMembershipYearsLabel = (startDate: string, endDate: string) =>
  `${printYear(startDate)}${NON_BREAKING_HYPHEN}${printYear(endDate)}`;

export const getLeadershipTermsLabel = (start: string, end: string) =>
  `${start}${NON_BREAKING_HYPHEN}${end}`;

const getMemberTermDates = (
  t: TFunction,
  startDate?: string,
  endDate?: string
) => {
  if (startDate) {
    if (endDate) {
      return getMembershipYearsLabel(startDate, endDate);
    }
    return t('roster-result.member.dates', '{{ date }}-present', {
      date: printYear(startDate),
    });
  }

  return null;
};

export const getMembershipDates = (
  hasLeadershipRole: boolean,
  t: TFunction,
  admissionDate?: string,
  deceasedDate?: Maybe<string>
) => {
  let startDate;
  let endDate;
  if (!hasLeadershipRole && admissionDate) {
    startDate = admissionDate;
    if (deceasedDate) {
      endDate = deceasedDate;
    }
  }

  return getMemberTermDates(t, startDate, endDate);
};

export const getLeadershipTerms = (terms: string[]) => {
  const formatedTerms = terms?.reduce((acc, currentTerm) => {
    const regExpSeparator = new RegExp(NON_BREAKING_HYPHEN);
    const terms = currentTerm.split(regExpSeparator);
    const lastTermIndex = acc.length - 1;
    const { start, end } = acc[lastTermIndex] || {};

    if (start === terms[1]) {
      acc.splice(lastTermIndex, 1, { start: terms[0], end });
    } else {
      acc.push({
        start: terms[0],
        end: terms[1],
      });
    }

    return acc;
  }, [] as { start: string; end: string }[]);

  return formatedTerms.map(({ start, end }) =>
    getLeadershipTermsLabel(start, end)
  );
};

export type MemberResultProps = {
  name: string;
  email: string;
  phone: number | string;
  type: string;
  date: string;
  status: string;
  role: string;
  id: string;
};

export type RosterResultProps = {
  clubType: string;
  clubId: string;
  isRotaractOfficer?: boolean;
  contextMenuItems: {
    id: number;
    label: string;
    path: string;
  }[];
  activeAssociatedClubs: Partial<Club>[];
  type: MembershipType | undefined;
  isDeceasedFiltered: boolean | undefined;
  isTerminatedFiltered: boolean | undefined;
  isLeadershipRole: boolean;
  dates: string | null;
  terminationHistory:
    | { terminationDates: string; terminationReason: string }[]
    | null;
  hasActiveMembershipInOtherClubs: boolean | null | undefined;
  isDeceasedOutsideOfClub: boolean | null | undefined;
  isTerminatedAndHasActiveMembershipInOtherClubs: boolean | null | undefined;
  activeMembership:
    | ({ __typename?: 'ActiveMembership' } & Pick<
        ActiveMembership,
        'type' | 'admissionDate' | 'terminationDate'
      >)
    | undefined;
  clubLeadershipInfo: AssignmentInfo[];
  admissionDate: string | undefined;
  appliedRoleFilter: string[];
  isNonMember?: boolean;
  activeLeaders: ActiveLeaders[];
};

type ContextMenuOptions = {
  operationsAccess: Pick<
    IndividualOperationsAccessGql,
    'assignRole' | 'editMember' | 'editMembershipType' | 'terminateMembership'
  >;
  isDeceased?: Maybe<boolean>;
  activeMembership:
    | Pick<ActiveMembership, 'type' | 'admissionDate' | 'terminationDate'>
    | undefined;
  id: string;
  clubId: string;
  membershipId?: Maybe<string>;
  clubType: string;
  roleId?: string;
  leadershipId?: string;
  startDate?: string;
  endDate?: string;
  isNonMember?: boolean;
  activeLeaders: ActiveLeaders[];
};

export type AssignmentInfo = {
  roleName: string;
  terms: string[];
};

export type LeadsMMLPage = {
  isLeads?: boolean;
  redirectUrl?: string;
  isThreeColumn?: boolean;
  selectMember: (id: string) => void;
};

export const getContextMenuItems = (
  t: TFunction,
  options: ContextMenuOptions
) => {
  const {
    operationsAccess,
    isDeceased,
    activeMembership,
    id,
    clubId,
    membershipId,
    clubType,
    roleId,
    leadershipId,
    startDate,
    endDate,
    isNonMember,
    activeLeaders,
  } = options;
  const {
    assignRole,
    terminateMembership,
    editMembershipType,
  } = operationsAccess;
  const contextMenuItems = [];

  const isDisabled = Boolean(activeLeaders.length < 2) && isToday(startDate);

  if (!isDeceased && (activeMembership || isNonMember)) {
    if (isEditLevel(assignRole) && !isNonMember) {
      contextMenuItems.push({
        id: 1,
        label: t(
          'roster-result.context-menu.assign-club-officer-role',
          'Assign a Club Officer Role'
        ),
        path: `${getOfficersPagePath(clubId)}/assign-role/${id}`,
      });
    }

    if (isEditLevel(editMembershipType)) {
      const isRotaractClub = isClubRotaract(clubType);
      contextMenuItems.push({
        id: 2,
        label: isRotaractClub
          ? t(
              'roster-result.context-menu.rotaract.edit-membership-type',
              'Edit membership'
            )
          : t(
              'roster-result.context-menu.edit-membership-type',
              'Edit membership and sponsorship'
            ),
        path: `/club/${clubId}/edit-membership/${membershipId}`,
      });
    }

    if (isEditLevel(terminateMembership)) {
      contextMenuItems.push({
        id: 3,
        label: t(
          'roster-result.context-menu.terminate-membership',
          'Terminate Membership'
        ),
        path: `/club/${clubId}/terminate-membership/${membershipId}`,
      });
    }
    if (
      (isEditLevel(editMembershipType) || isNonMember) &&
      leadershipId &&
      roleId &&
      activeLeaders
    ) {
      contextMenuItems.push({
        id: 4,
        label: t(
          'roster-result.context-menu.replace-with-someone-else',
          'Replace with someone Else'
        ),
        path:
          activeLeaders.length > 1
            ? `${getOfficersPagePath(clubId)}/replace-role/${id}`
            : `/club/${clubId}/officers/replace-member/${id}/${leadershipId}/${roleId}/replace/${startDate}/${endDate}`,
        isDisabled,
      });
    }
  }

  return contextMenuItems;
};

export const mapRoleLabelForRotaractors = (
  t: TFunction,
  roleLabel: string | undefined
) => {
  switch (roleLabel) {
    case MembershipType.Rotaractor:
      return t('club-membership.type.rotaractor', 'Member');
    case MembershipType.RotaractHonorary:
      return t('club-membership.type.rotaract-honorary', 'Honorary');
    case MembershipType.Member:
      return t('club-membership.type.rotary.member', 'Member');
    case MembershipType.Honorary:
      return t('club-membership.type.rotary-honorary', 'Honorary');
    default:
      return roleLabel;
  }
};
